import { onMounted } from 'vue'
import { ref, watch, ComputedRef, onBeforeUnmount } from 'vue'

interface UseTruncationOptions {
  items?: ComputedRef<any[]> | (() => any[])
  debounceTime?: number
}

export default function useTruncation(options: UseTruncationOptions = {}) {
  const { items = () => [], debounceTime = 200 } = options

  // Estado para modo múltiple
  const isTruncatedItems = ref<boolean[]>([])
  const itemRefs = ref<any[]>([])

  // Estado para modo individual
  const isTruncated = ref<boolean>(false)
  const elementRef = ref<HTMLElement | null>(null)

  // Variable para almacenar el timeout del debounce
  let debounceTimeout: ReturnType<typeof setTimeout> | null = null

  onMounted(() => {
    const itemsArray = getItemsArray()
    initializeRefs(itemsArray)
  })

  // Limpieza automática al desmontar
  onBeforeUnmount(() => {
    cleanup()
  })

  // Función auxiliar para extraer el array de la ref computada o función
  const getItemsArray = (): any[] => {
    if (typeof items === 'function') {
      return items()
    } else {
      return items.value
    }
  }

  const checkTruncation = () => {
    // Implementación de debounce interno
    if (debounceTimeout !== null) {
      clearTimeout(debounceTimeout)
    }

    debounceTimeout = setTimeout(() => {
      // Reiniciar el estado de truncamiento para nueva evaluación
      isTruncatedItems.value = []

      // Verificar cada elemento usando referencias Vue
      itemRefs.value.forEach((element, index) => {
        if (element) {
          const isTruncated = element.scrollWidth > element.offsetWidth
          isTruncatedItems.value[index] = isTruncated
        } else {
          isTruncatedItems.value[index] = false
        }
      })

      debounceTimeout = null
    }, debounceTime)
  }

  // Función para verificar el truncamiento de un solo elemento
  const checkElementTruncation = () => {
    if (!elementRef.value) return;
    isTruncated.value = elementRef.value.scrollWidth > elementRef.value.offsetWidth
  }

  const initializeRefs = (items: any[] = []) => {
    // Inicializar array de referencias con el tamaño correcto
    itemRefs.value = Array(items.length).fill(null)
  }

  const setRef = (el: any, index: number) => {
    if (el) itemRefs.value[index] = el
  }

  // Función de limpieza para debounce
  const cleanup = () => {
    if (debounceTimeout !== null) {
      clearTimeout(debounceTimeout)
      debounceTimeout = null
    }
  }

  // Vigilar cambios en los elementos
  watch(
    () => getItemsArray(),
    (newItems) => {
      if (newItems) {
        initializeRefs(newItems)
      }
    },
    { immediate: true, deep: true }
  )

  return {
    isTruncatedItems,
    itemRefs,
    checkTruncation,
    initializeRefs,
    setRef,
    checkElementTruncation,
    isTruncated,
    elementRef,
    cleanup
  }
}
